<script setup>
import { computed } from 'vue'
import { Icon } from '@iconify/vue'

/**
 * AppButton — single source of truth for all button styles in CDP 2.0.
 *
 * Variants (from Figma node 23327:53807):
 *   primary   — vivid-blue → protoss-pylon gradient, white text
 *   default   — solid blue-500, white text
 *   secondary — blue-50 bg, blue-500 text
 *   tertiary  — gray-50 bg; coloured=true → blue-500 text, coloured=false → gray text
 *   outlined  — white bg, gray-200 border, gray-700 text; coloured=true → tint-1 border and text
 *   link      — no bg/border; coloured=true → blue text, tint-2 under the pointer; coloured=false → gray text
 *   dashed    — white bg, dashed gray-200 border, gray text
 *   header    — pill for the coloured (blue) app header bar: translucent dark
 *               fill, blue-600 border, white text, rounded-lg. Shared by the
 *               "Add Cards" / "Switch to old view" / AI-credits header buttons.
 *   danger-link — red text, no fill until hover (secondary destructive action)
 *
 * ⚠️ Template must stay SINGLE-ROOT: no comments at the template root (before/
 * between the split `v-if` and standard `v-else`). A root-level comment makes
 * Vue render AppButton as a fragment in dev, so `$el` points at the comment node
 * and HeadlessUI trigger focus (`MenuButton`/`PopoverButton`) throws
 * "e3.focus is not a function" on close.
 *
 * Sizes:  sm (h-7, text-xs)  |  md (h-[34px], text-sm)  |  lg (h-10, text-sm)
 * Icons:  leadingIcon  — any Iconify icon name on the left
 *         dropdown     — shows chevron-down on the right
 *         iconClass    — extra classes on leadingIcon / trailingIcon / the chevron (a stroke width, a colour)
 *         split        — adds a visual divider before a SECOND action section (split-button
 *                        pattern) whose click emits 'dropdown-click' separately from the main
 *                        'click'. The section's icon is the dropdown chevron by default, but
 *                        `splitIcon` can swap it for any Iconify icon (e.g. a `+` add-split like
 *                        the Batches quick action). Works with or without `dropdown`.
 */
const props = defineProps({
  variant:      { type: String,  default: 'primary' },
  // 'primary' | 'default' | 'secondary' | 'tertiary' | 'outlined' | 'link' | 'dashed' | 'header'

  size:         { type: String,  default: 'md' },     // 'sm' | 'md' | 'lg'
  disabled:     { type: Boolean, default: false },
  loading:      { type: Boolean, default: false },
  // link + tertiary: blue unless false; outlined: gray unless true (Figma "Outlined / Coloured? Yes"). Null = each variant's own default.
  coloured:     { type: [Boolean, null], default: null },
  leadingIcon:  { type: String,  default: null },      // Iconify icon name, e.g. 'heroicons:plus'
  trailingIcon: { type: String,  default: null },      // Iconify icon name on the right edge (e.g. 'heroicons:chevron-right-16-solid' for Next-style buttons). Mutually exclusive with `dropdown`; `dropdown` wins if both are set.
  iconClass:    { type: String,  default: '' },        // extra classes on leadingIcon, trailingIcon and the dropdown chevron
  dropdown:     { type: Boolean, default: false },     // show chevron-down
  open:         { type: Boolean, default: false },     // chevron rotates 180° when true (pass the panel's open state)
  split:        { type: Boolean, default: false },     // split button: divider + separate secondary-section click
  splitDivider: { type: Boolean, default: true },      // show/hide the divider line between label and the split section
  splitIcon:    { type: String,  default: 'heroicons:chevron-down-16-solid' }, // icon in the split section — default dropdown chevron; pass e.g. 'heroicons:plus-16-solid' for an add-split
  as:           { type: [String, Object], default: 'button' },
  type:         { type: String,  default: 'button' },  // html button type
  rounded:      { type: String,  default: null },      // 'md' | 'lg' | 'xl' | 'full' — override corner radius (default: md; header variant defaults to lg)
})

const emit = defineEmits(['click', 'dropdown-click'])

const isDisabled = computed(() => props.disabled || props.loading)
// Split mode no longer requires `dropdown` — a split section can be an add
// (`+`), share, etc. via `splitIcon`. `dropdown` only controls chevron rotation.
const isSplit    = computed(() => props.split)

// ── Size ─────────────────────────────────────────────────────────────────────

// Figma's Button sizes S / M / B. `lg` is the 40px button: 20px beside the label, 10px above and below.
const SIZE = {
  sm: { box: 'h-7',      pad: 'px-2 py-1 text-xs',   chevron: 16 },
  md: { box: 'h-[34px]', pad: 'p-2 text-sm',         chevron: 18 },
  lg: { box: 'h-10',     pad: 'px-5 py-2.5 text-sm', chevron: 18 },
}
const size = computed(() => SIZE[props.size] ?? SIZE.md)

const sizeClass = computed(() => `${size.value.box} ${size.value.pad}`)
// In split mode the height lives on the outer wrapper (which owns the border) so
// border-box keeps the total height correct (h-7 = 28px incl. border, not 30px).
// The inner buttons then fill the wrapper (`h-full`) and only apply padding/text.
const splitHeightClass = computed(() => size.value.box)
const splitInnerClass  = computed(() => size.value.pad)
const iconSize    = 16
const chevronSize = computed(() => size.value.chevron)

// ── Corner radius ─────────────────────────────────────────────────────────────
// `rounded` prop overrides the base `rounded-md`. Literal classes (not
// `!rounded-${r}`) so Tailwind's JIT scanner detects them. The `header` variant
// defaults to `lg` unless the caller passes an explicit `rounded`.
const ROUNDED = { md: '!rounded-md', lg: '!rounded-lg', xl: '!rounded-xl', full: '!rounded-full' }
const roundedClass = computed(() => {
  const r = props.rounded || (props.variant === 'header' ? 'lg' : null)
  return r ? (ROUNDED[r] ?? '') : ''
})

// ── Variant colours ───────────────────────────────────────────────────────────

const ACTIVE = {
  primary:    'bg-gradient-to-r from-vivid-blue to-protoss-pylon text-white hover:from-[#0015dc] hover:to-[#0087ce]',
  default:    'bg-tint-1 text-white hover:shadow-[0_0_5px_#69a0ff]',
  secondary:  'bg-blue-50 text-tint-1 hover:shadow-[0_0_5px_#69a0ff]',
  tertiary:   'bg-gray-50 text-tint-1 hover:shadow-[0_0_5px_#69a0ff]',
  outlined:   'bg-white border border-gray-200 text-gray-700 hover:border-blue-200 hover:bg-white hover:shadow-[0_0_5px_#69a0ff]',
  dashed:     'bg-white border border-dashed border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-blue-200 hover:shadow-[0_0_5px_#69a0ff]',
  success:    'bg-green-500 text-white hover:bg-green-600',
  danger:     'bg-red-600 text-white hover:bg-red-700',
  // Destructive text button — no fill until hover. For secondary destructive
  // actions that sit next to a primary CTA (e.g. "End listings" beside
  // "List Items"), where a solid red `danger` would out-shout the primary.
  'danger-link': 'bg-white text-red-600 hover:bg-red-50',
  // Header-bar pill (on the blue header): translucent dark fill + blue-600
  // border + white text. `!` overrides the base `rounded-md` + `p-2` padding.
  header:     'bg-gray-800/10 border border-blue-600 text-white hover:bg-gray-800/20 !px-3',
  // Tab-toggle variants (pill-style view switcher)
  'tab':      'text-gray-600 hover:bg-gray-100 rounded-[8px] w-20 cursor-pointer focus:!ring-0',
  'tab-active':'bg-blue-50 border border-tint-1 rounded-[6px] text-tint-1 font-semibold w-20 cursor-pointer focus:!ring-0',
}

const DISABLED = {
  primary:    'bg-gradient-to-r from-[rgba(32,53,255,0.5)] to-[rgba(1,168,255,0.5)] text-white',
  default:    'bg-gradient-to-r from-[rgba(32,53,255,0.5)] to-[rgba(1,168,255,0.5)] text-white',
  secondary:  'bg-blue-50 text-blue-400',
  tertiary:   'bg-gray-50 text-blue-400',
  outlined:   'bg-white border border-gray-50 text-gray-300',
  dashed:     'bg-white border border-dashed border-gray-50 text-gray-300',
  success:    'bg-green-200 text-white',
  danger:     'bg-red-200 text-white',
  'danger-link': 'bg-white text-red-300',
  header:     'bg-gray-800/5 border border-blue-600/40 text-white/50 !px-3',
  'tab':      'text-gray-300 rounded-[8px] w-20',
  'tab-active':'bg-blue-50 border border-blue-200 text-blue-300 rounded-[8px] w-20',
}

// An absent `coloured` keeps each variant's own look: blue link and tertiary, gray outlined.
const isColoured = computed(() => props.coloured ?? props.variant !== 'outlined')

const linkActive    = computed(() => isColoured.value
  ? 'bg-white text-tint-1 hover:text-tint-2 hover:shadow-[0_0_5px_#e5e5e5]'
  : 'bg-white text-gray-500 hover:bg-gray-50'
)
const linkDisabled  = computed(() => isColoured.value ? 'bg-white text-blue-100' : 'bg-white text-gray-300')

// `tertiary` with `coloured=false`: the same gray-50 fill with gray content (Figma "Light Gray / Coloured? No").
const isGrayTertiary = computed(() => props.variant === 'tertiary' && !isColoured.value)
const GRAY_TERTIARY  = { active: 'bg-gray-50 text-gray-600 hover:bg-gray-200', disabled: 'bg-gray-50 text-gray-400' }

// `outlined` with `coloured`: white with a tint-1 border and text, blue-50 under the pointer (Figma "Outlined / Coloured? Yes").
const isBlueOutlined = computed(() => props.variant === 'outlined' && isColoured.value)
const BLUE_OUTLINED  = { active: 'bg-white border border-tint-1 text-tint-1 hover:bg-blue-50', disabled: 'bg-white border border-tint-1 text-blue-300' }

const variantClass = computed(() => {
  if (props.variant === 'link') return isDisabled.value ? linkDisabled.value : linkActive.value
  if (isGrayTertiary.value) return isDisabled.value ? GRAY_TERTIARY.disabled : GRAY_TERTIARY.active
  if (isBlueOutlined.value) return isDisabled.value ? BLUE_OUTLINED.disabled : BLUE_OUTLINED.active
  return isDisabled.value
    ? (DISABLED[props.variant] ?? DISABLED.outlined)
    : (ACTIVE[props.variant]   ?? ACTIVE.outlined)
})

// ── Divider colour between split sections ─────────────────────────────────────
const dividerClass = computed(() => {
  switch (props.variant) {
    case 'primary':
    case 'default':
    case 'header':    return 'bg-white/30'
    case 'secondary': return 'bg-blue-200'
    case 'tertiary':  return isGrayTertiary.value ? 'bg-gray-300' : 'bg-blue-200'
    case 'outlined':  return isBlueOutlined.value ? 'bg-blue-200' : 'bg-gray-200'
    default:          return 'bg-gray-200'
  }
})

// ── Shared base (everything except bg/border which comes from variantClass) ───
const baseClass = 'inline-flex items-center justify-center gap-1 rounded-md font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 whitespace-nowrap'
const cursorClass = computed(() => isDisabled.value ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer')
</script>

<template>
  <div
    v-if="isSplit"
    class="inline-flex items-stretch rounded-md overflow-hidden box-border"
    :class="[variantClass, cursorClass, roundedClass, splitHeightClass]"
  >
    <!-- Main action -->
    <button
      :type="type"
      :disabled="isDisabled"
      class="inline-flex items-center gap-1 h-full font-medium transition duration-200 focus:outline-none cursor-pointer"
      :class="splitInnerClass"
      @click="emit('click', $event)"
    >
      <Icon
        v-if="leadingIcon && !loading"
        :icon="leadingIcon"
        :width="iconSize"
        :height="iconSize"
        class="shrink-0"
        :class="iconClass"
      />
      <Icon
        v-else-if="loading"
        icon="svg-spinners:3-dots-fade"
        :width="iconSize"
        :height="iconSize"
        class="shrink-0"
      />
      <slot />
    </button>

    <!-- Divider -->
    <span v-if="splitDivider" class="self-stretch w-px my-1" :class="dividerClass" />

    <!-- Dropdown trigger -->
    <button
      :type="type"
      :disabled="isDisabled"
      class="inline-flex items-center justify-center transition duration-200 focus:outline-none px-1.5 cursor-pointer"
      @click.stop="emit('dropdown-click', $event)"
    >
      <Icon
        :icon="splitIcon"
        :width="chevronSize"
        :height="chevronSize"
        class="shrink-0 transition-transform duration-200"
        :class="{ 'rotate-180': open && dropdown }"
      />
    </button>
  </div>

  <component
    :is="as"
    v-else
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? isDisabled : undefined"
    :aria-disabled="isDisabled || undefined"
    class="inline-flex items-center justify-center gap-1 rounded-md font-medium transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 whitespace-nowrap"
    :class="[sizeClass, variantClass, cursorClass, roundedClass]"
    @click="!isDisabled && emit('click', $event)"
  >
    <Icon
      v-if="leadingIcon && !loading"
      :icon="leadingIcon"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
      :class="iconClass"
    />
    <Icon
      v-else-if="loading"
      icon="svg-spinners:3-dots-fade"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
    />
    <slot />
    <Icon
      v-if="dropdown"
      icon="heroicons:chevron-down-16-solid"
      :width="chevronSize"
      :height="chevronSize"
      class="shrink-0 transition-transform duration-200"
      :class="[iconClass, { 'rotate-180': open }]"
    />
    <Icon
      v-else-if="trailingIcon"
      :icon="trailingIcon"
      :width="iconSize"
      :height="iconSize"
      class="shrink-0"
      :class="iconClass"
    />
  </component>
</template>
